export type ApiErrorPayload = {
  code: string | null;
  details: unknown;
  message: string;
  retryAfterSeconds: number | null;
  status: number;
};

function readString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function parseRetryAfterSeconds(value: string | null) {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const seconds = Number.parseFloat(trimmed);
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.floor(seconds);
  }

  const timestamp = Date.parse(trimmed);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  const diffMs = timestamp - Date.now();
  if (diffMs <= 0) {
    return null;
  }

  return Math.ceil(diffMs / 1_000);
}

export async function parseApiError(
  response: Response,
  defaultMessage = 'REQUEST_FAILED'
): Promise<ApiErrorPayload> {
  let code: string | null = null;
  let details: unknown = null;
  let message = response.statusText || defaultMessage;
  try {
    const data = (await response.json()) as Record<string, unknown>;
    code = readString(data.code);
    details = data.details ?? null;
    message = readString(data.error) ?? readString(data.message) ?? message;
  } catch {
    // Ignore non-JSON responses.
  }

  return {
    code,
    details,
    message,
    retryAfterSeconds: parseRetryAfterSeconds(
      response.headers.get('Retry-After')
    ),
    status: response.status,
  };
}
